/**
 * Choice: the one select. Options are a typed list of {key, label}; the popover and the list are React Aria's, the
 * paint is the palette's. The trigger has a LOOK: a field (the label above a full-width button holding the value,
 * the forms' select) or a pill (the trigger a caller words, raised or outlined: the cockpit's category switch and
 * kind picks). The popover and the list are the same under both.
 */
import type { CSSProperties, KeyboardEvent, ReactNode } from 'react';
import type { Key } from 'react-aria-components';
import {
  Button,
  Label,
  ListBox,
  ListBoxItem,
  Popover,
  Select,
  SelectValue,
  type SelectProps,
} from 'react-aria-components';
import { BRAND, CONTROL, DIM, FRAME, RADIUS, SPACE, TEXT, WASH_ALPHA, WEIGHT, Z, tint, usePalette, type Palette } from '../theme';
import { fieldLabelStyle } from './field';
import { focusRing } from './action-button';
import { OUTLINE, chipStyle } from './chip';

export type ChoiceOption<K extends Key> = { key: K; label: ReactNode };

/** A pill trigger's shape: raised and square-cornered (the bar's switch), or the outlined pill. */
export type PillShape = 'raised' | 'outline';

/** How a Choice's trigger looks and what it says. */
export type ChoiceLook =
  | {
      readonly kind: 'field';
      readonly size: 'sm' | 'md';
      /** Whether the label is read by assistive technology only. */
      readonly labelHidden: boolean;
    }
  | {
      readonly kind: 'pill';
      readonly shape: PillShape;
      /** The trigger's accessible name: what it picks and what it says now, so it contains the words it shows (label in name). */
      readonly name: string;
      /** What the trigger says. */
      readonly says: ReactNode;
    };

function pillStyle(p: Palette, shape: PillShape, focused: boolean): CSSProperties {
  const form: CSSProperties =
    shape === 'raised'
      ? {
          padding: `${SPACE.tight}px ${SPACE.snug}px ${SPACE.tight}px ${SPACE.md}px`,
          borderRadius: RADIUS.button,
          border: '1px solid transparent',
          background: p.surfaceRaised,
          color: p.ink,
          font: 'inherit',
          fontSize: TEXT.body,
          lineHeight: 'normal',
          whiteSpace: 'nowrap',
          boxSizing: 'border-box',
        }
      : { ...chipStyle(p, OUTLINE, 'bar', false), fontSize: TEXT.micro };
  return {
    ...form,
    display: 'inline-flex',
    alignItems: 'center',
    gap: SPACE.sm,
    cursor: 'pointer',
    outline: 'none',
    boxShadow: focused ? focusRing(p) : undefined,
  };
}

/**
 * A letter pressed on the closed pill. React Aria's select would pick the first option that begins with it (an
 * "a" chooses "article head"), and on the walk a pick is a filed approval: the letters belong to the open list's
 * search, never to the trigger, so the press stops here and neither the select nor a window shortcut sees it.
 */
function refuseLetterOnTrigger(event: KeyboardEvent<HTMLElement>) {
  const onTrigger = event.target instanceof HTMLElement && event.target.closest('[aria-haspopup="listbox"]') !== null;
  if (onTrigger && event.key.length === 1 && event.key !== ' ' && !event.metaKey && !event.ctrlKey && !event.altKey) {
    event.stopPropagation();
  }
}

/** The open list, the same under every look: a popover at least `minWidth` wide holding the options. */
function ChoiceList<K extends Key>({ label, options, minWidth }: { label: string; options: readonly ChoiceOption<K>[]; minWidth: string | number }) {
  const p = usePalette();
  return (
    <Popover
      offset={4}
      style={{
        zIndex: Z.popover,
        minWidth,
        padding: SPACE.xs,
        borderRadius: RADIUS.md,
        border: `1px solid ${p.hairline}`,
        background: p.surface,
        boxShadow: `0 12px 32px -8px ${p.shadow}`,
      }}
    >
      <ListBox
        aria-label={label}
        items={options}
        style={{ display: 'flex', flexDirection: 'column', gap: SPACE.xxs, maxHeight: FRAME.popoverMax, overflow: 'auto', outline: 'none' }}
      >
        {(item) => (
          <ListBoxItem
            id={item.key}
            textValue={String(item.label)}
            style={(s) => ({
              padding: `${CONTROL.padYCompact}px ${CONTROL.padXCompact}px`,
              borderRadius: RADIUS.sm,
              fontSize: TEXT.control,
              color: s.isSelected ? p.brandInk : p.ink,
              fontWeight: s.isSelected ? WEIGHT.strong : WEIGHT.regular,
              background: s.isFocused || s.isHovered ? tint(BRAND.teal, WASH_ALPHA.menuHover) : 'transparent',
              cursor: s.isDisabled ? 'not-allowed' : 'pointer',
              opacity: s.isDisabled ? DIM.disabled : 1,
              outline: 'none',
            })}
          >
            {item.label}
          </ListBoxItem>
        )}
      </ListBox>
    </Popover>
  );
}

export function Choice<K extends Key>({
  label,
  options,
  look,
  ...rest
}: Omit<SelectProps<ChoiceOption<K>>, 'className' | 'style' | 'children' | 'aria-label'> & {
  /** What it picks: the field's visible label, and the pill's list name. */
  label: string;
  options: readonly ChoiceOption<K>[];
  look: ChoiceLook;
}) {
  const p = usePalette();
  switch (look.kind) {
    case 'field': {
      const small = look.size === 'sm';
      return (
        <Select {...rest} style={{ display: 'flex', flexDirection: 'column', gap: SPACE.xs, minWidth: 0 }}>
          <Label style={fieldLabelStyle(p, look.labelHidden)}>{label}</Label>
          <Button
            style={(s) =>
              ({
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: SPACE.sm,
                height: small ? CONTROL.heightSm : CONTROL.height,
                padding: `0 ${small ? CONTROL.padXSm : CONTROL.padX}px`,
                borderRadius: RADIUS.md,
                border: `1px solid ${s.isHovered ? p.inkMuted : p.hairline}`,
                background: p.surface,
                color: p.ink,
                font: 'inherit',
                fontSize: small ? TEXT.control : TEXT.body,
                cursor: s.isDisabled ? 'not-allowed' : 'pointer',
                opacity: s.isDisabled ? DIM.disabled : 1,
                minWidth: 0,
                width: '100%',
                boxSizing: 'border-box',
                outline: 'none',
                boxShadow: s.isFocusVisible ? focusRing(p) : undefined,
              }) satisfies CSSProperties
            }
          >
            <SelectValue
              style={({ isPlaceholder }) => ({
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                color: isPlaceholder ? p.inkMuted : p.ink,
              })}
            />
            <span aria-hidden="true" style={{ color: p.inkMuted, fontSize: TEXT.caption }}>
              ▾
            </span>
          </Button>
          <ChoiceList label={label} options={options} minWidth="var(--trigger-width)" />
        </Select>
      );
    }
    case 'pill':
      return (
        <span style={{ display: 'inline-flex' }} onKeyDownCapture={refuseLetterOnTrigger}>
          <Select {...rest} aria-label={label}>
            <Button aria-label={look.name} style={(s) => pillStyle(p, look.shape, s.isFocusVisible)}>
              {look.says}
            </Button>
            <ChoiceList label={label} options={options} minWidth={CONTROL.listMin} />
          </Select>
        </span>
      );
  }
}
